import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface ApexPromoStatus {
  is_active: boolean;
  discount_percent: number | null;
  coupon_code: string;
  headline: string;
  last_checked_at: string;
  override_active: boolean;
  override_discount_percent: number | null;
  sale_ends_at: string | null;
}

const CACHE_KEY = "apex_promo_status_cache";

const DEFAULT_PROMO: ApexPromoStatus = {
  is_active: false,
  discount_percent: null,
  coupon_code: "Next-level",
  headline: "No active sale",
  last_checked_at: new Date().toISOString(),
  override_active: false,
  override_discount_percent: null,
  sale_ends_at: null,
};

/**
 * Read the last known promo state synchronously so the urgency banner renders
 * on the first paint instead of appearing later and shifting the page down.
 */
function readCachedPromo(): ApexPromoStatus | null {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as ApexPromoStatus) : null;
  } catch {
    return null;
  }
}

export function useApexPromoStatus() {
  const cached = typeof window !== "undefined" ? readCachedPromo() : null;
  const [promo, setPromo] = useState<ApexPromoStatus>(cached ?? DEFAULT_PROMO);
  const [loading, setLoading] = useState(!cached);

  useEffect(() => {
    const fetchPromo = async () => {
      const { data, error } = await supabase
        .from("apex_promo_status")
        .select(
          "is_active, discount_percent, coupon_code, headline, last_checked_at, override_active, override_discount_percent, sale_ends_at"
        )
        .eq("id", "00000000-0000-0000-0000-000000000001")
        .single();

      if (error) {
        console.error("Error fetching promo status:", error);
      } else if (data) {
        const raw = data as ApexPromoStatus;
        // Apply override on the client so the page reflects it immediately
        const effective: ApexPromoStatus = raw.override_active
          ? {
              ...raw,
              is_active: true,
              discount_percent: raw.override_discount_percent ?? raw.discount_percent,
              headline: `${raw.override_discount_percent ?? raw.discount_percent ?? ""}% off active`,
            }
          : raw;
        setPromo(effective);
        try {
          window.localStorage.setItem(CACHE_KEY, JSON.stringify(effective));
        } catch {
          /* storage unavailable — non-fatal */
        }
      }
      setLoading(false);
    };

    fetchPromo();
  }, []);

  return { promo, loading };
}
